// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadBlackOutDays from "@hardwoods/App/Handlers/Checkout/BlackOutDays";
import loadFirstAvailableDate from "@hardwoods/App/Handlers/Checkout/FirstAvailableDate";
import { xCheckoutState } from "@hardwoods/App/Store/Reducers";
import { color } from "@hardwoods/XDL/Utilities";
import { isValidDate } from "@hardwoods/XDL/Utilities/helper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: xCheckoutState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const {
        pages: {
            checkoutReviewAndSubmit: { requestedDeliveryDate, requestedPickupDate },
        },
    } = state;
    return {
        cart: cartState.value,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            session.fulfillmentMethod === FulfillmentMethod.PickUp,
        cartSettings: settingsCollection.cartSettings,
        firstAvailableDate: state.xCheckout.firstAvailableDate,
        blackoutDays: state.xCheckout.blackoutDays || [],
        requestedDeliveryDate,
        requestedPickupDate,
    };
};

const mapDispatchToProps = {
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    loadFirstAvailableDate,
    loadBlackOutDays,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & WidgetProps;

export interface PreferredShippingDateStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    headingGridItem?: GridItemProps;
    headingTitle?: TypographyPresentationProps;
    optionalLabel?: TypographyPresentationProps;
    datePicker?: DatePickerPresentationProps;
}

export const preferredShippingDateStyles: PreferredShippingDateStyles = {
    container: {
        gap: 10,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding-bottom: 10px;
            border-bottom: 1px solid ${color("common.border")};
        `,
    },
    headingTitle: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
        `,
    },
    optionalLabel: {
        weight: "normal",
        transform: "capitalize",
        css: css`
            font-style: oblique;
        `,
    },
    gridItem: { width: 12 },
};

const styles = preferredShippingDateStyles;

const CheckoutShippingHeader: FC<Props> = ({
    cart,
    showPickUpDate,
    cartSettings,
    setRequestedPickUpDate,
    setRequestedDeliveryDate,
    loadFirstAvailableDate,
    loadBlackOutDays,
    blackoutDays,
    firstAvailableDate,
    requestedDeliveryDate,
    requestedPickupDate,
}) => {
    const [currentDate] = React.useState(
        new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()),
    );

    React.useEffect(() => {
        loadFirstAvailableDate({});
        loadBlackOutDays({});
    }, []);

    React.useEffect(() => {
        let date;
        if (cartSettings.canRequestDeliveryDate && !showPickUpDate) {
            date = requestedDeliveryDate ? new Date(requestedDeliveryDate) :
                cart?.requestedDeliveryDate ? new Date(cart.requestedDeliveryDate) :
                    firstAvailableDate ? new Date(firstAvailableDate) :
                        undefined;
            setRequestedDeliveryDate({ requestedDeliveryDate: date, });
        } else if (showPickUpDate) {
            date = requestedPickupDate ? new Date(requestedPickupDate) :
                cart?.requestedPickupDate ? new Date(cart.requestedPickupDate) :
                    firstAvailableDate ? new Date(firstAvailableDate) :
                        undefined;
            setRequestedPickUpDate({ requestedPickUpDate: date, });
        }
    }, [cart, firstAvailableDate]);

    const handleRequestPickUpDateChanged = ({ selectedDay }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
        });
    };

    if ((!cartSettings.canRequestDeliveryDate && !showPickUpDate) || !cart) {
        return null;
    }

    const datePickerProps = {
        minDate: firstAvailableDate ? new Date(firstAvailableDate) : addDays(currentDate, 1),
        maxDate: addDays(currentDate, cartSettings.maximumDeliveryPeriod),
        tileDisabled: (value: any) => {
            const date = new Date(value.date);
            return !isValidDate(blackoutDays, date);
        },
        clearIcon: null,
        ...styles.datePicker?.dateTimePickerProps,
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.headingTitle}>
                    {showPickUpDate
                        ? `${translate("Preferred Pickup Date")}`
                        : `${translate("Preferred Shipping Date")}`}
                    &nbsp;
                    <Typography {...styles.optionalLabel}>({translate("optional")})</Typography>
                </Typography>
            </GridItem>
            {cartSettings.canRequestDeliveryDate && !showPickUpDate && (
                <GridItem {...styles.gridItem} data-test-selector="checkoutShippingRequestedDeliveryDate">
                    <DatePicker
                        label={`${translate("Select Preferred Shipping Date")}`}
                        hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                        {...styles.datePicker}
                        selectedDay={
                            requestedDeliveryDate ??
                            ((requestedDeliveryDate && new Date(cart.requestedDeliveryDate)) || undefined)
                        }
                        dateTimePickerProps={datePickerProps}
                        onDayChange={handleRequestDeliveryDateChanged}
                    />
                </GridItem>
            )}
            {showPickUpDate && (
                <>
                    <GridItem {...styles.gridItem} data-test-selector="checkoutPickUpRequestedDeliveryDate">
                        <DatePicker
                            label={`${translate("Select Preferred Pick Up Date")}`}
                            hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                            {...styles.datePicker}
                            selectedDay={
                                requestedPickupDate ??
                                ((requestedPickupDate && new Date(cart.requestedPickupDate)) || undefined)
                            }
                            dateTimePickerProps={datePickerProps}
                            onDayChange={handleRequestPickUpDateChanged}
                        />
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutShippingHeader),
    definition: {
        displayName: "Preferred Shipping Date",
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage"],
    },
};

export default widgetModule;
